import { useCallback, useEffect, useRef, useState } from 'react';
import {
  mediaDocumentsApi,
  subscribeMediaDocumentsChanged,
  type MediaDocumentsData,
} from './mediaDocuments';

/** Presigned URLs gelten 15 min — rechtzeitig vorher neu laden. */
const REFRESH_MS = 10 * 60 * 1000;

export interface UseMediaDocuments {
  data: MediaDocumentsData | null;
  loading: boolean;
  error: boolean;
  /** silent=true: kein Lade-Zustand (Hintergrund-Refresh). */
  reload: (silent?: boolean) => Promise<void>;
  /** Zeitpunkt des letzten erfolgreichen Ladens (ms). */
  loadedAt: number;
}

/**
 * Lädt Medien + Dokumente eines Users (v2) und hält sie aktuell:
 * Refresh alle 10 min (URL-Ablauf) und bei Änderungen aus anderen Ansichten
 * (notifyMediaDocumentsChanged).
 */
export function useMediaDocuments(userId: number | null | undefined): UseMediaDocuments {
  const [data, setData] = useState<MediaDocumentsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [loadedAt, setLoadedAt] = useState(0);
  const reqId = useRef(0);

  const reload = useCallback(async (silent = false) => {
    if (userId == null) return;
    const id = ++reqId.current;
    if (!silent) setLoading(true);
    try {
      const res = await mediaDocumentsApi.list(userId);
      if (id !== reqId.current) return;
      setData(res);
      setError(false);
      setLoadedAt(Date.now());
    } catch {
      if (id !== reqId.current) return;
      setError(true);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setData(null);
    setError(false);
    if (userId == null) return undefined;
    void reload();
    const unsubscribe = subscribeMediaDocumentsChanged(userId, () => { void reload(true); });
    const iv = window.setInterval(() => { void reload(true); }, REFRESH_MS);
    return () => {
      unsubscribe();
      window.clearInterval(iv);
      reqId.current++;
    };
  }, [userId, reload]);

  return { data, loading, error, reload, loadedAt };
}
